import React from 'react';
import ExperimentContext from '@prisjakt/ui-experiment/context';
import { putSession } from '../globals/api/requests';

type Experiment<T> = {
  id: string;
  baseline: T;
  variants: { [key: string]: T };
};

type State = {
  applicationExperiments: Array<Experiment<any>>;
  selectedVariants: SelectedVariants;
};

export type SelectedVariants = {
  [key: string]: {
    variantIndex: number;
    selectedVariant: string;
  };
};

export type ExperimentSession = {
  env?: 'dev' | 'production';
  optimize?: string;
  experiments?: SelectedVariants;
};

const ExperimentContextContainer = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const [state, setState] = React.useState<State>({
    applicationExperiments: [],
    selectedVariants: {},
  });

  const [experimentSession, setExperimentSession] = React.useState<
    ExperimentSession
  >({});

  React.useEffect(() => {
    // Explicitly set experiments here, as the app was hydrated
    // under the assumption that only baseline was used in all experiments.
    // TODO: This can be removed when we have server side experiments.
    setExperimentSession(window.SESSION_DATA.experiments);
  }, [setExperimentSession]);

  const { session } = window.SESSION_DATA;

  const devMode = experimentSession.env === 'dev';

  const value = React.useMemo(
    () => ({
      showEditor: devMode,
      sessionId: session.id,
      selectedVariants: {
        ...experimentSession.experiments,
        ...state.selectedVariants,
      },
      experiments: state.applicationExperiments,
      registerExperiment: (experiment: Experiment<any>) => {
        if (!devMode) {
          return;
        }

        setState(currState => {
          if (
            currState.applicationExperiments.find(e => e.id === experiment.id)
          ) {
            return currState;
          }
          return {
            ...currState,
            applicationExperiments: [
              ...currState.applicationExperiments,
              experiment,
            ],
          };
        });
      },
      selectedVariant: (experiment: Experiment<any>, variant: string) => {
        putSession('selectedVariants', {
          [experiment.id]: variant,
        });
        setState(currState => ({
          ...currState,
          selectedVariants: {
            ...currState.selectedVariants,
            [experiment.id]: {
              ...experiment,
              selectedVariant: variant,
              variantIndex: -1,
            },
          },
        }));
      },
    }),
    [
      devMode,
      experimentSession.experiments,
      session.id,
      state.applicationExperiments,
      state.selectedVariants,
    ]
  );
  return (
    <ExperimentContext.Provider value={value}>
      {children}
    </ExperimentContext.Provider>
  );
};

export default ExperimentContextContainer;
